/**
 * The tab's `sessionStorage`, as a Zustand `StateStorage` that never throws
 * (spec multi-product 007 §2.1 "Firm per tab", board unit WP1.24.8).
 *
 * WHY sessionStorage AND NOT localStorage. The active firm is PER TAB: a person
 * in two firms can hold one in each tab, and each tab's requests must claim its
 * own. `localStorage` is shared by every tab of the origin, so a switch in one
 * tab would silently re-point the other's next request at a different firm.
 * `sessionStorage` is scoped to the tab (and survives its reloads), which is
 * exactly the lifetime the claim needs.
 *
 * WHY EVERY CALL IS GUARDED. Storage can be absent (the server render, where
 * this module is also evaluated), blocked (privacy settings, a sandboxed
 * frame), full (quota), or throw on access in a private window. Losing the
 * stored firm only means the next request carries no claim, which is today's
 * behaviour (the server's scope rule picks the firm), so every failure degrades
 * to "nothing stored" instead of breaking the page.
 *
 * The storage is READ AT CALL TIME through `resolve`, never captured: a test
 * hands each simulated tab its own storage, and the browser's is only looked
 * up inside the `try`.
 */
import type { StateStorage } from "zustand/middleware"

export const tabSessionStorage = (
  resolve: () => Storage = () => globalThis.sessionStorage,
): StateStorage => ({
  getItem: (name) => {
    try {
      return resolve().getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      resolve().setItem(name, value)
    } catch {
      // Quota, blocked or absent storage: the firm lives in memory for this page only.
    }
  },
  removeItem: (name) => {
    try {
      resolve().removeItem(name)
    } catch {
      // Nothing stored to remove, or storage is unreachable: either way nothing persists.
    }
  },
})
